import { RangeParams } from './range-params';

/**
 * Source of a calculation trigger. The facade uses this to decide whether
 * to debounce, whether the call is cancellable, and where to route the
 * result.
 *
 * - USER_INPUT: a slider drag, waypoint move, car/charging-station change.
 *   Debounced 400 ms; latest emission cancels in-flight prior calc.
 * - NAVIGATION_START: nav-start handler. Fires immediately, not debounced.
 * - REPLAN: off-route detection, skip-waypoint, skip-charger. Fires
 *   immediately, not debounced.
 * - BETTER_ROUTE_CHECK: 10-min nav timer. Runs on parallel stream; never
 *   cancels the main user-input calc and is never cancelled by it.
 * - GPS_DRIFT: range mode re-anchored the start to the moving GPS fix and
 *   nothing else changed. Same debounced pipeline as USER_INPUT, but the UI
 *   treats it as a background refresh: the range is redrawn in place and the
 *   camera is left alone.
 */
export enum CalculationIntent {
  USER_INPUT = 'USER_INPUT',
  NAVIGATION_START = 'NAVIGATION_START',
  REPLAN = 'REPLAN',
  BETTER_ROUTE_CHECK = 'BETTER_ROUTE_CHECK',
  GPS_DRIFT = 'GPS_DRIFT',
}

export type CalculationState =
  | { status: 'idle' }
  | { status: 'pending'; intent: CalculationIntent; kind: 'route' | 'range'; rangeParams: RangeParams }
  | { status: 'success'; intent: CalculationIntent; kind: 'route'; data: any; rangeParams: RangeParams }
  | { status: 'success'; intent: CalculationIntent; kind: 'range'; data: any; rangeParams: RangeParams }
  | { status: 'error'; intent: CalculationIntent; kind: 'route' | 'range'; error: CalculationError; rangeParams: RangeParams };

export type CalculationError =
  | { kind: 'country_not_supported' }
  | { kind: 'closest_route_too_far' }
  // source: 'server' — server returned error.message='can_not_calculate_route';
  // triggers navigation-aware skip-charger / exit-navigation branch.
  // source: 'invalid_response' — server returned empty/null payload;
  // does NOT trigger the nav-aware branch (preserves current behaviour).
  | { kind: 'cannot_calculate_route'; maxReachedWaypoint: number; source: 'server' | 'invalid_response'; waypointsInfo?: any[] }
  | { kind: 'cannot_calculate_range'; source: 'invalid_response' | 'network' }
  | { kind: 'route_limit_exceeded' }
  | { kind: 'free_tier_limit' }
  | { kind: 'route_network'; cause: unknown }
  // Selected vehicle has no compatible charger types configured.
  // Side effect: open the "select charging station" dialog. No map clear.
  | { kind: 'no_charger_types' };

const ROUTE_HARD_LIMIT_KM = 4500;
const FREE_TIER_LIMIT_KM = 500;

export type DistanceCalculator = (a: [number, number], b: [number, number]) => number;

export function validateRouteRequest(
  rangeParams: RangeParams,
  isFreeTier: boolean,
  distanceBetweenCoordinates: DistanceCalculator,
): CalculationError | null {
  let totalMeters = 0;
  for (let i = 0; i < rangeParams.Waypoints.length - 1; i++) {
    totalMeters += distanceBetweenCoordinates(
      [rangeParams.Waypoints[i].lat, rangeParams.Waypoints[i].lng],
      [rangeParams.Waypoints[i + 1].lat, rangeParams.Waypoints[i + 1].lng],
    );
  }
  const totalKm = totalMeters / 1000;
  if (totalKm > ROUTE_HARD_LIMIT_KM) {
    return { kind: 'route_limit_exceeded' };
  }
  if (totalKm > FREE_TIER_LIMIT_KM && isFreeTier) {
    return { kind: 'free_tier_limit' };
  }
  return null;
}

export function mapRouteResponse(resp: any): CalculationError | null {
  if (resp?.error === 'country_not_supported') {
    return { kind: 'country_not_supported' };
  }
  if (resp?.error?.message === 'can_not_calculate_route') {
    return {
      kind: 'cannot_calculate_route',
      maxReachedWaypoint: resp.error.maxReachedWaypoint ?? 0,
      source: 'server',
      waypointsInfo: resp.waypointsInfo,
    };
  }
  if (resp == null || resp.route == null || resp.route.length === 0) {
    return {
      kind: 'cannot_calculate_route',
      maxReachedWaypoint: 0,
      source: 'invalid_response',
      waypointsInfo: resp?.waypointsInfo,
    };
  }
  return null;
}

export function mapRangeResponse(resp: any): CalculationError | null {
  if (resp?.error === 'country_not_supported') {
    return { kind: 'country_not_supported' };
  }
  if (resp?.error === 'closest_route_too_far') {
    return { kind: 'closest_route_too_far' };
  }
  if (
    resp == null ||
    resp.length === 0 ||
    resp.range1 === undefined ||
    (resp.range1.length === 0 && resp.range2.length === 0)
  ) {
    return { kind: 'cannot_calculate_range', source: 'invalid_response' };
  }
  return null;
}

export function mapRouteHttpError(err: unknown): CalculationError {
  return { kind: 'route_network', cause: err };
}

export function mapRangeHttpError(_err: unknown): CalculationError {
  return { kind: 'cannot_calculate_range', source: 'network' };
}
